import React, { Fragment, useState, useEffect } from "react";
import { ListGroup, Form, Modal } from "react-bootstrap";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { getSignedUrl } from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { useParams } from "react-router-dom";
import renderLatex from "../../../add-new-course/steps/quiz-genaration/RenderLatex";

// Image Modal Component for full-screen image display
const ImageModal = ({ show, onHide, imageUrl, alt }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen={true}
      className="p-0"
      style={{ zIndex: 50000 }}
    >
      <Modal.Header className="border-0 bg-dark position-relative">
        <Modal.Title className="text-white fs-5">{alt}</Modal.Title>
        {/* Custom close button - large, thick, and clearly visible */}
        <button
          type="button"
          onClick={onHide}
          aria-label="Close"
          className="position-absolute top-0 end-0 m-3"
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '2.5rem',
            fontWeight: '300',
            lineHeight: '1',
            cursor: 'pointer',
            opacity: '0.9',
            transition: 'opacity 0.2s, transform 0.2s',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ×
        </button>
      </Modal.Header>
      <Modal.Body 
        className="p-0 d-flex align-items-center justify-content-center bg-dark"
        style={{ overflow: 'hidden' }}
      >
        <img
          src={imageUrl}
          alt={alt}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            width: 'auto',
            height: 'auto',
          }}
          className="mx-auto"
        />
      </Modal.Body>
    </Modal>
  );
};
// Image Loader Component
const ImageLoader = ({ imageUrl, loading, error, alt, className, size = "md", showNoImage = false }) => {
  const [showModal, setShowModal] = useState(false);

  const sizeStyles = {
    md: { width: '64px', height: '64px' },
    xl: { width: '200px', height: '200px' }
  };

  if (loading) {
    return (
      <div 
        style={sizeStyles[size]}
        className={`d-flex align-items-center justify-content-center bg-light rounded ${className}`}
      >
        <div 
          className="spinner-border spinner-border-sm text-primary" 
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        style={sizeStyles[size]}
        className={`d-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded ${className}`}
      >
        <span className="text-danger small">Error</span>
      </div>
    );
  }

  if (!imageUrl) {
    if (!showNoImage) {
      return null;
    }
    return (
      <div 
        style={sizeStyles[size]}
        className={`d-flex align-items-center justify-content-center bg-light rounded ${className}`}
      >
        <span className="text-muted small">No image</span>
      </div>
    );
  }

  return (
    <>
      <div 
        style={sizeStyles[size]}
        className={`position-relative ${className}`}
        onClick={() => setShowModal(true)}
        role="button"
        tabIndex={0}
      >
        <img
          src={imageUrl}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            cursor: 'pointer'
          }}
          className="rounded border"
        />
        <div 
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
          style={{
            backgroundColor: 'rgba(0,0,0,0)',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0)'}
        >
          <ZoomInIcon 
            className="text-white"
            fontSize="small"
            style={{ opacity: 0, transition: 'opacity 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
          />
        </div>
      </div>
      
      <ImageModal
        show={showModal}
        onHide={() => setShowModal(false)}
        imageUrl={imageUrl}
        alt={alt}
      />
    </>
  );
};

export const SingleQuestion = ({
  item,
  selectedAnswer,
  onAnswerChange,
  isReview,
  correctAnswers,
  trackQUestionClick,
  result,
  courseId: propCourseId,
  sectionId: propSectionId,
  userId: propUserId,
}) => {
  const [selectedValue, setSelectedValue] = useState(selectedAnswer);
  const type = item.interface === "checkbox" ? "checkbox" : "radio";
  const params = useParams();
  const courseId = propCourseId || params.courseId;
  const sectionId = propSectionId || params.sectionId;
  const [cookies] = useCookies(["userId"]);
  const userId = propUserId || cookies?.userId;
  const [questionImageUrls, setQuestionImageUrls] = useState([]);
  const [questionLoading, setQuestionLoading] = useState({});
  const [questionErrors, setQuestionErrors] = useState({});
  const [answerImageUrls, setAnswerImageUrls] = useState({});
  const [answerLoading, setAnswerLoading] = useState({});
  const [answerErrors, setAnswerErrors] = useState({});

  const [shuffledAnswers, setShuffledAnswers] = useState(() => {
    if (item?.answers && item.answers.length > 0) {
      let shuffled = [...item.answers];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    }
    return [];
  });

  useEffect(() => {
    if (item?.answers && item.answers.length > 0 && shuffledAnswers.length === 0) {
      let shuffled = [...item.answers];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setShuffledAnswers(shuffled);
    }
  }, [item?.answers, shuffledAnswers.length]);

  const activeAnswers = shuffledAnswers.length > 0 ? shuffledAnswers : (item?.answers || []);

  useEffect(() => {
    setSelectedValue(selectedAnswer);
  }, [selectedAnswer]);

  const handleAnswerSelect = (value) => {
    if (!isReview) {
      setSelectedValue(value);
      onAnswerChange(value);
    }
  };

  const getCorrectAnswerValue = () => {
    if (result?.modal_ans !== undefined) return String(result.modal_ans);
    if (result?.ans !== undefined) return String(result.ans);
    if (item?.correctAnswer !== undefined) return String(item.correctAnswer);
    const legacyCorrect = correctAnswers?.find((entry) => entry.questionId === item.id.toString());
    if (legacyCorrect?.correctValue !== undefined) return String(legacyCorrect.correctValue);
    if (result?.correctValue !== undefined) return String(result.correctValue);
    return null;
  };

  const getUserAnswerValue = () => {
    if (result?.userAnswer !== undefined && result.userAnswer !== "") return String(result.userAnswer);
    if (selectedValue !== undefined && selectedValue !== null) return String(selectedValue);
    return null;
  };

  const getStyles = (answer) => {
    if (!isReview) return { text: "", radio: "" };

    const correctVal = getCorrectAnswerValue();
    const userVal = getUserAnswerValue();
    const ansValStr = String(answer.value);

    const isTrueFalse = String(correctVal).toLowerCase() === "true" || String(correctVal).toLowerCase() === "false";
    
    if (correctVal === ansValStr || (isTrueFalse && String(correctVal).toLowerCase() === String(ansValStr).toLowerCase())) {
      return { text: "text-success fw-bold", radio: "btn-success" };
    }
    
    if (userVal === ansValStr && userVal !== correctVal) {
      const isActuallyCorrect = result?.evaluation?.is_correct || (result?.final_score ?? result?.evaluation?.score) > 0 || result?.status === "correct";
      if (!isActuallyCorrect) {
        return { text: "text-danger fw-bold", radio: "btn-danger" };
      }
    }
    return { text: "", radio: "" };
  };

  // Fetch question images
  useEffect(() => {
    const fetchQuestionImageUrls = async () => {
      const imageNames = Array.isArray(item.imageQuestion) ? item.imageQuestion : [];
      if (!imageNames.length) {
        setQuestionImageUrls([]);
        setQuestionLoading({});
        setQuestionErrors({});
        return;
      }

      setQuestionLoading(Object.fromEntries(imageNames.map((name) => [name, true])));
      setQuestionErrors({});

      const promises = imageNames.map(async (imageName, index) => {
        const payload = {
          courseId: courseId,
          sectionId: sectionId,
          fileName: imageName,
          method: "GET",
          userId: userId,
        };
        try {
          const response = await getSignedUrl(payload);
          {/* console.log(`Question - Fetched question image ${imageName}:`, response.data.preSignedUrl); */}
          return { imageName, url: response.data.preSignedUrl };
        } catch (error) {
          {/* console.error(`Question - Error fetching question image ${imageName}:`, error); */}
          setQuestionErrors((prev) => ({
            ...prev,
            [imageName]: "Failed to load question image",
          }));
          return { imageName, url: null };
        } finally {
          setQuestionLoading((prev) => ({
            ...prev,
            [imageName]: false,
          }));
        }
      });

      const results = await Promise.all(promises);
      setQuestionImageUrls(results.filter((result) => result.url));
    };
    fetchQuestionImageUrls();
  }, [courseId, sectionId, item.imageQuestion, cookies?.userId]);

  // Fetch answer images
  useEffect(() => {
    const fetchAnswerImageUrls = async () => {
      const promises = item.answers.flatMap((answer) =>
        (Array.isArray(answer.imageOptionName) ? answer.imageOptionName : []).map(async (imageName) => {
          if (!imageName) {
            setAnswerImageUrls((prev) => ({
              ...prev,
              [answer.value]: prev[answer.value] ? [...prev[answer.value]] : [],
            }));
            setAnswerLoading((prev) => ({
              ...prev,
              [answer.value]: { ...prev[answer.value], [imageName]: false },
            }));
            return;
          }

          setAnswerLoading((prev) => ({
            ...prev,
            [answer.value]: { ...prev[answer.value], [imageName]: true },
          }));
          setAnswerErrors((prev) => ({
            ...prev,
            [answer.value]: { ...prev[answer.value], [imageName]: null },
          }));

          const payload = {
            courseId: courseId,
            sectionId: sectionId,
            fileName: imageName,
            method: "GET",
            userId: userId,
          };
          try {
            const response = await getSignedUrl(payload);
            {/* console.log(`Question - Fetched answer ${answer.value} image ${imageName}:`, response.data.preSignedUrl); */}
            setAnswerImageUrls((prev) => ({
              ...prev,
              [answer.value]: [...(prev[answer.value] || []), { imageName, url: response.data.preSignedUrl }],
            }));
          } catch (error) {
            {/* console.error(`Question - Error fetching answer ${answer.value} image ${imageName}:`, error); */}
            setAnswerErrors((prev) => ({
              ...prev,
              [answer.value]: { ...prev[answer.value], [imageName]: "Failed to load answer image" },
            }));
          } finally {
            setAnswerLoading((prev) => ({
              ...prev,
              [answer.value]: { ...prev[answer.value], [imageName]: false },
            }));
          }
        })
      );

      await Promise.all(promises);
    };
    fetchAnswerImageUrls();
  }, [courseId, sectionId, item.answers, cookies?.userId]);

  return (
    <Fragment>
      {/* Question Header */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h3 className="fs-5 fw-semibold mb-0" dangerouslySetInnerHTML={{ __html: renderLatex(item.question) }} />
          {isReview && (result?.evaluation || result?.final_score !== undefined || result?.score !== undefined) && (
            <span
              className="px-2 py-1 rounded"
              style={{
                backgroundColor:
                  (result.final_score ?? result.evaluation?.overall_score ?? result.evaluation?.score ?? result.score) === (result.total_marks ?? item.point ?? 1)
                    ? "#e6f4ea"
                    : (result.final_score ?? result.evaluation?.overall_score ?? result.evaluation?.score ?? result.score) === 0
                    ? "#fce8e6"
                    : "#e8f0fe",
                color:
                  (result.final_score ?? result.evaluation?.overall_score ?? result.evaluation?.score ?? result.score) === (result.total_marks ?? item.point ?? 1)
                    ? "#137333"
                    : (result.final_score ?? result.evaluation?.overall_score ?? result.evaluation?.score ?? result.score) === 0
                    ? "#c5221f"
                    : "#1967d2",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                marginLeft: "15px",
                fontSize: "0.9rem"
              }}
            >
              {result.final_score ?? result.evaluation?.overall_score ?? result.evaluation?.score ?? result.score} / {result.total_marks ?? item.point ?? 1} Score
            </span>
          )}
        </div>
        
        {/* Question Images - Multiple images, centered, clickable */}
        {questionImageUrls.length > 0 || Object.keys(questionLoading).length > 0 || Object.keys(questionErrors).length > 0 ? (
          <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
            {item.imageQuestion?.map((imageName, index) => (
              <ImageLoader
                key={`${imageName}-${index}`}
                imageUrl={questionImageUrls.find((img) => img.imageName === imageName)?.url}
                loading={questionLoading[imageName]}
                error={questionErrors[imageName]}
                alt={`Question Image ${index + 1}`}
                size="xl"
                showNoImage={true}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Answers Section */}
      {item.interface === "text" || item.answerSelectionType === "fill_in_the_blanks" ? (
        <div className="mt-3">
          <Form.Control
            type="text"
            placeholder="Type your answer here"
            value={selectedValue || ""}
            onChange={(e) => {
              if (!isReview) {
                handleAnswerSelect(e.target.value);
                trackQUestionClick(item);
              }
            }}
            readOnly={isReview}
            className={
              isReview
                ? ((result.final_score ?? result.evaluation?.overall_score) > 0 && (result.final_score ?? result.evaluation?.overall_score) < result?.total_marks)
                  ? "border border-2 border-info text-info"
                  : (result?.evaluation?.is_correct || result?.status === "correct" || result?.isCorrect)
                    ? "border border-2 border-success text-success"
                    : "border border-2 border-danger text-danger"
                : ""
            }
          />
          {isReview && (
            <div className="mt-2 d-flex align-items-center gap-2">
              {((result.final_score ?? result.evaluation?.overall_score) > 0 && (result.final_score ?? result.evaluation?.overall_score) < result?.total_marks) ? (
                <span title="Partially Correct" style={{ color: "#0dcaf0", fontWeight: "bold", fontSize: "1.5rem", lineHeight: 1 }}>~</span>
              ) : (result?.evaluation?.is_correct || result?.status === "correct" || result?.isCorrect) ? (
                <DoneIcon sx={{ color: "green", fontSize: 28 }} />
              ) : (
                <span style={{ color: "red", fontWeight: "bold", fontSize: "1.5rem", paddingRight: "5px", lineHeight: 1 }}>!</span>
              )}
              <span className="text-success small fw-medium">
                Correct Answer: <span dangerouslySetInnerHTML={{ __html: renderLatex(result?.modal_ans || result?.correctValue || item.correctAnswer || "Not provided") }} />
              </span>
            </div>
          )}
        </div>
      ) : item.interface === "textarea" || item.answerSelectionType === "subjective" ? (
        <div className="mt-3">
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Type your detailed answer here"
            value={selectedValue || ""}
            onChange={(e) => {
              if (!isReview) {
                handleAnswerSelect(e.target.value);
                trackQUestionClick(item);
              }
            }}
            readOnly={isReview}
            className={
              isReview
                ? ((result.final_score ?? result.evaluation?.overall_score) > 0 && (result.final_score ?? result.evaluation?.overall_score) < result?.total_marks)
                  ? "border border-2 border-info text-info"
                  : (result?.evaluation?.is_correct || result?.status === "correct" || result?.isCorrect)
                    ? "border border-2 border-success text-success"
                    : "border border-2 border-danger text-danger"
                : ""
            }
          />
          {isReview && (
            <div className="mt-2 d-flex align-items-center gap-2">
              {((result.final_score ?? result.evaluation?.overall_score) > 0 && (result.final_score ?? result.evaluation?.overall_score) < result?.total_marks) ? (
                <span title="Partially Correct" style={{ color: "#0dcaf0", fontWeight: "bold", fontSize: "1.5rem", lineHeight: 1 }}>~</span>
              ) : (result?.evaluation?.is_correct || result?.status === "correct" || result?.isCorrect) ? (
                <DoneIcon sx={{ color: "green", fontSize: 28 }} />
              ) : (
                <span style={{ color: "red", fontWeight: "bold", fontSize: "1.5rem", paddingRight: "5px", lineHeight: 1 }}>!</span>
              )}
              <span className="text-success small fw-medium">
                Correct Answer: <span dangerouslySetInnerHTML={{ __html: renderLatex(result?.modal_ans || result?.correctValue || item.correctAnswer || "Not provided") }} />
              </span>
            </div>
          )}
        </div>
      ) : item.interface === "button" ? (
        <div className="row g-3">
          {activeAnswers.map((answer, renderIndex) => {
            const originalIndex = item.answers.findIndex((a) => a.value === answer.value);
            const index = originalIndex;
            const styles = getStyles(answer);
            const answerImages = answerImageUrls[answer.value] || [];
            
            return (
              <div key={index} className="col-12 col-sm-6">
                <Form.Check
                  type="radio"
                  id={`radio-${answer.value}`}
                  className="d-grid"
                  onClick={() => handleAnswerSelect(answer.value)}
                >
                  <Form.Check.Input
                    type="radio"
                    className={`btn-check ${styles.radio}`}
                    name={"answer-" + item.id}
                    checked={selectedValue === answer.value}
                    readOnly
                  />
                  <Form.Check.Label
                    className={`btn btn-outline-secondary text-start d-flex align-items-center gap-2 ${styles.text}`}
                    style={{ cursor: 'pointer' }}
                  >
                    {answerImages.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {answer.imageOptionName?.map((imageName, imgIndex) => (
                          <ImageLoader
                            key={`${imageName}-${imgIndex}`}
                            imageUrl={answerImages.find((img) => img.imageName === imageName)?.url}
                            loading={answerLoading[answer.value]?.[imageName]}
                            error={answerErrors[answer.value]?.[imageName]}
                            alt={`Option ${answer.value} Image ${imgIndex + 1}`}
                            size="md"
                            showNoImage={false}
                          />
                        ))}
                      </div>
                    ) : null}
                    <span dangerouslySetInnerHTML={{ __html: renderLatex(answer.answer) }} />
                  </Form.Check.Label>
                </Form.Check>
              </div>
            );
          })}
        </div>
      ) : (
        <ListGroup>
          {activeAnswers.map((answer, renderIndex) => {
            const originalIndex = item.answers.findIndex((a) => a.value === answer.value);
            const index = originalIndex;
            const styles = getStyles(answer);
            const answerImages = answerImageUrls[answer.value] || [];
            
            return (
              <ListGroup.Item
                key={index}
                className={`list-group-item-action ${styles.text} d-flex justify-content-between align-items-center`}
                style={{ cursor: !isReview ? 'pointer' : 'default' }}
                onClick={() => {
                  if (!isReview) {
                    handleAnswerSelect(answer.value);
                    trackQUestionClick(item);
                  }
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type={type}
                    id={answer.value}
                    name={"answer-" + item.id}
                    label={<span dangerouslySetInnerHTML={{ __html: renderLatex(answer.answer) }} />}
                    value={answer.value}
                    checked={
                      isReview
                        ? getCorrectAnswerValue() === String(answer.value)
                        : Array.isArray(selectedValue)
                        ? selectedValue.includes(answer.value)
                        : String(selectedValue) === String(answer.value)
                    }
                    className={`${styles.radio}`}
                    readOnly
                  />
                  {answerImages.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {answer.imageOptionName?.map((imageName, imgIndex) => (
                        <ImageLoader
                          key={`${imageName}-${imgIndex}`}
                          imageUrl={answerImages.find((img) => img.imageName === imageName)?.url}
                          loading={answerLoading[answer.value]?.[imageName]}
                          error={answerErrors[answer.value]?.[imageName]}
                          alt={`Option ${answer.value} Image ${imgIndex + 1}`}
                          size="md"
                          showNoImage={false}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
                {isReview && getCorrectAnswerValue() === String(answer.value) ? (
                  <DoneIcon sx={{ color: "green", fontSize: 28 }} />
                ) : null}
                {isReview && getUserAnswerValue() === String(answer.value) && getUserAnswerValue() !== getCorrectAnswerValue() && !(result?.evaluation?.is_correct || result?.evaluation?.score > 0 || result?.status === "correct") ? (
                  <span style={{ color: "red", fontWeight: "bold", fontSize: "1.5rem", paddingRight: "5px", lineHeight: 1 }}>!</span>
                ) : null}
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}
      {isReview && item.explanation && (
        <div className="mt-2 small" style={{ color: "#000" }}>
          <strong>Explanation:</strong> <span dangerouslySetInnerHTML={{ __html: renderLatex(item.explanation) }} />
        </div>
      )}
    </Fragment>
  );
};

export default SingleQuestion;